import { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { InstallPermissionAccessTypes } from '../constants/installPermissions';
import { jsx as _jsx } from "react/jsx-runtime";
const InstallPermissionsContext = /*#__PURE__*/createContext(null);
export function InstallPermissionsProvider({
  children,
  initialInstallPermissionAccessState = InstallPermissionAccessTypes.SUPER_ADMINS_AND_AUTHORIZED_INSTALLERS,
  entityTypeAndIds = []
}) {
  const initialUserIds = useMemo(() => {
    return entityTypeAndIds.reduce((acc, entity) => {
      if (entity.entityType === 'USER') {
        acc.add(entity.entityId);
      }
      return acc;
    }, new Set());
  }, [entityTypeAndIds]);
  const initialTeamIds = useMemo(() => {
    return entityTypeAndIds.reduce((acc, entity) => {
      if (entity.entityType === 'TEAM') {
        acc.add(entity.entityId);
      }
      return acc;
    }, new Set());
  }, [entityTypeAndIds]);
  const [installPermissionAccessState, setInstallPermissionAccessState] = useState(initialInstallPermissionAccessState);
  const [updatedUsers, setUpdatedUsers] = useState({});
  const [updatedTeams, setUpdatedTeams] = useState({});
  const previousPermissionStateRef = useRef(installPermissionAccessState);

  // Keeps track of previous state to understand when the permission type changes
  useEffect(() => {
    const previousPermissionState = previousPermissionStateRef.current;
    const isNewPermissionState = previousPermissionState !== installPermissionAccessState;

    // Clear updated users when permission type changes to maintain consistent state
    if (isNewPermissionState) {
      const changingFromSpecificUsersAndTeams = previousPermissionState === InstallPermissionAccessTypes.SPECIFIC_USERS_AND_TEAMS;
      // If we're changing from SPECIFIC_USERS_AND_TEAMS to a different permission state, we need to clear the updated users
      if (changingFromSpecificUsersAndTeams) {
        setUpdatedUsers({});
        setUpdatedTeams({});
      }
    }
    previousPermissionStateRef.current = installPermissionAccessState;
  }, [installPermissionAccessState]);
  const resetUpdatedUsers = useCallback(() => {
    setUpdatedUsers({});
    setUpdatedTeams({});
    setInstallPermissionAccessState(initialInstallPermissionAccessState);
  }, [initialInstallPermissionAccessState]);
  const getSelectedEntityTypeAndIds = useCallback(() => {
    let selectedEntityTypeAndIds = [];
    const isSpecificUsersAndTeams = installPermissionAccessState === 'SPECIFIC_USERS_AND_TEAMS';
    if (isSpecificUsersAndTeams) {
      const selectedUserIds = new Set(initialUserIds);
      Object.entries(updatedUsers).forEach(([userIdStr, isSelected]) => {
        const userId = Number(userIdStr);
        if (isSelected) {
          selectedUserIds.add(userId);
        } else {
          selectedUserIds.delete(userId);
        }
      });
      selectedEntityTypeAndIds = Array.from(selectedUserIds, userId => ({
        entityType: 'USER',
        entityId: userId
      }));
    }
    return selectedEntityTypeAndIds;
  }, [initialUserIds, updatedUsers, installPermissionAccessState]);
  const updatedUsersCount = useMemo(() => {
    const isSpecificUsersAndTeams = installPermissionAccessState === 'SPECIFIC_USERS_AND_TEAMS';
    if (!isSpecificUsersAndTeams) {
      return 0;
    }
    return Object.keys(updatedUsers).length;
  }, [updatedUsers, installPermissionAccessState]);
  const updatedTeamsCount = useMemo(() => {
    const isSpecificUsersAndTeams = installPermissionAccessState === 'SPECIFIC_USERS_AND_TEAMS';
    if (!isSpecificUsersAndTeams) {
      return 0;
    }
    return Object.keys(updatedTeams).length;
  }, [updatedTeams, installPermissionAccessState]);
  const hasChanges = useMemo(() => {
    const stateChanged = installPermissionAccessState !== initialInstallPermissionAccessState;
    const usersChanged = updatedUsersCount > 0;
    const teamsChanged = updatedTeamsCount > 0;
    return stateChanged || usersChanged || teamsChanged;
  }, [installPermissionAccessState, initialInstallPermissionAccessState, updatedUsersCount, updatedTeamsCount]);
  const value = useMemo(() => ({
    updatedUsers,
    setUpdatedUsers,
    updatedUsersCount,
    updatedTeams,
    setUpdatedTeams,
    updatedTeamsCount,
    hasChanges,
    initialUserIds,
    initialTeamIds,
    installPermissionAccessState,
    initialInstallPermissionAccessState,
    setInstallPermissionAccessState,
    getSelectedEntityTypeAndIds,
    resetUpdatedUsers
  }), [updatedUsers, updatedUsersCount, updatedTeams, updatedTeamsCount, hasChanges, initialUserIds, initialTeamIds, installPermissionAccessState, initialInstallPermissionAccessState, getSelectedEntityTypeAndIds, resetUpdatedUsers]);
  return /*#__PURE__*/_jsx(InstallPermissionsContext.Provider, {
    value: value,
    children: children
  });
}
export function useInstallPermissions() {
  const context = useContext(InstallPermissionsContext);
  if (!context) {
    throw new Error('useInstallPermissions must be used within InstallPermissionsProvider');
  }
  return context;
}